import { t as dedupe } from "./dedupe-Cky1gSUu.js";
import { _ as KeboolaHttpHeader, c as defaultValidateStatus, r as isApiError, s as HttpHeader } from "./createFetchClient-CkHdS_IG.js";
import { n as ManagementClientAuthError, t as ManagementSessionMintError } from "./ManagementSessionMintError-4Pkn5Oob.js";
import { t as createGenericFetchClient } from "./createGenericFetchClient-CQdNcPC3.js";
import { t as createOpenapiFetchClient } from "./createOpenapiFetchClient-DXiMv9WL.js";
import { t as isLoginPageRedirect } from "./isLoginPageRedirect-B4QKSiSa.js";
//#region src/clients/management/applicationTokens/applicationTokens.ts
const createApplicationTokens = (client) => {
	const getApplicationTokens = async (signal) => {
		const { data } = await client.get("/manage/application-tokens", {}, { signal });
		return data;
	};
	const getApplicationTokenScopes = async (signal) => {
		const { data } = await client.get("/manage/application-tokens/scopes", {}, { signal });
		return data;
	};
	const createApplicationToken = async (body) => {
		const { data } = await client.post("/manage/application-tokens", { body });
		return data;
	};
	const deleteApplicationToken = async (tokenId) => {
		await client.delete("/manage/application-tokens/{id}", { path: { id: tokenId } });
	};
	return {
		getApplicationTokens,
		getApplicationTokenScopes,
		createApplicationToken,
		deleteApplicationToken
	};
};
//#endregion
//#region src/clients/management/validateStatus.ts
/**
* Status validation for the Manage API, which answers an expired session with the login page.
*
* - A *successful* login page is the followed redirect, and becomes `ManagementClientAuthError`.
* - HTML on a failed response is a real error page (a proxy's 502) and stays an `ApiError`: reading
*   it as session expiry would sign the user out over a transient upstream blip.
*/
const validateManagementStatus = (apiResponse) => {
	const isValid = defaultValidateStatus(apiResponse);
	if (isValid && isLoginPageRedirect(apiResponse)) return new ManagementClientAuthError("Your session expired", "You will be logged out automatically.", apiResponse);
	return isValid;
};
//#endregion
//#region src/clients/management/authMiddleware.ts
const TOKEN_CACHE_TTL_MS = 6e4;
const isManagementClientAuthError = (error) => error instanceof ManagementClientAuthError;
const isManagementSessionMintError = (error) => error instanceof ManagementSessionMintError;
const createAuth = (baseUrl) => {
	const fetchClient = createGenericFetchClient({
		baseUrl,
		validateStatus: validateManagementStatus
	});
	const verifyManagementToken = async (accessToken) => {
		const headers = new Headers({ [KeboolaHttpHeader.MANAGEMENT_API_TOKEN]: accessToken });
		const { data } = await fetchClient.get("/manage/tokens/verify", {}, { headers });
		return data;
	};
	const createCurrentUserSessionToken = async (accessToken) => {
		const headers = new Headers({ [KeboolaHttpHeader.MANAGEMENT_API_TOKEN]: accessToken });
		const { data } = await fetchClient.post("/manage/current-user/session-token", {}, { headers });
		return data.token;
	};
	const createAdminAccountSessionToken = async () => {
		try {
			const { data } = await fetchClient.post("/admin/account/session-token", {});
			return data.token;
		} catch (error) {
			if (isManagementClientAuthError(error)) throw new ManagementSessionMintError(error);
			throw error;
		}
	};
	return {
		verifyManagementToken,
		createCurrentUserSessionToken,
		createAdminAccountSessionToken
	};
};
/**
* Manage token auth for the legacy (SAPI) client: verifies, and mints a session token when needed.
*
* @deprecated Goes with the legacy entry point. A bearer client carries its credential and installs
* no auth middleware; delete this, `ManagementSessionMintError` and `manageSessionExpiredMiddleware`
* in kbc-ui once every stack is on bearer.
*/
const createAuthMiddleware = ({ accessToken, baseUrl, mintSessionToken = false }) => {
	const auth = createAuth(baseUrl);
	let token = mintSessionToken ? void 0 : accessToken;
	let tokenValidUntil = 0;
	const isTokenValid = async (candidate) => {
		try {
			const info = await auth.verifyManagementToken(candidate);
			return !info.isDisabled && !info.isExpired;
		} catch (error) {
			if (isApiError(error) && error.response.status === 401) return false;
			throw error;
		}
	};
	const createSessionToken = () => mintSessionToken && accessToken ? auth.createCurrentUserSessionToken(accessToken) : auth.createAdminAccountSessionToken();
	const ensureValidToken = async () => {
		if (token && Date.now() < tokenValidUntil) return token;
		if (token && await isTokenValid(token)) {
			tokenValidUntil = Date.now() + TOKEN_CACHE_TTL_MS;
			return token;
		}
		token = await createSessionToken();
		tokenValidUntil = Date.now() + TOKEN_CACHE_TTL_MS;
		return token;
	};
	const ensureValidTokenDeduped = dedupe(ensureValidToken);
	return (next) => async (request) => {
		const validToken = await ensureValidTokenDeduped();
		request.request.headers.set(KeboolaHttpHeader.MANAGEMENT_API_TOKEN, validToken);
		return next(request);
	};
};
//#endregion
//#region src/clients/management/currentUser/currentUser.ts
const createCurrentUser = (client) => {
	const getMaintainerInvitations = async (signal) => {
		const { data } = await client.get("/manage/current-user/maintainers-invitations", {}, { signal });
		return data;
	};
	const getMaintainerInvitation = async (id, signal) => {
		const { data } = await client.get("/manage/current-user/maintainers-invitations/{id}", { path: { id } }, { signal });
		return data;
	};
	const acceptMaintainerInvitation = async (id) => {
		await client.put("/manage/current-user/maintainers-invitations/{id}", { path: { id } });
	};
	const rejectMaintainerInvitation = async (id) => {
		await client.delete("/manage/current-user/maintainers-invitations/{id}", { path: { id } });
	};
	const getOrganizationInvitations = async (signal) => {
		const { data } = await client.get("/manage/current-user/organizations-invitations", {}, { signal });
		return data;
	};
	const getOrganizationInvitation = async (id, signal) => {
		const { data } = await client.get("/manage/current-user/organizations-invitations/{id}", { path: { id } }, { signal });
		return data;
	};
	const acceptOrganizationInvitation = async (id) => {
		await client.put("/manage/current-user/organizations-invitations/{id}", { path: { id } });
	};
	const rejectOrganizationInvitation = async (id) => {
		await client.delete("/manage/current-user/organizations-invitations/{id}", { path: { id } });
	};
	const getProjectInvitations = async (signal) => {
		const { data } = await client.get("/manage/current-user/projects-invitations", {}, { signal });
		return data;
	};
	const getProjectInvitation = async (id, signal) => {
		const { data } = await client.get("/manage/current-user/projects-invitations/{id}", { path: { id } }, { signal });
		return data;
	};
	const acceptProjectInvitation = async (id) => {
		await client.put("/manage/current-user/projects-invitations/{id}", { path: { id } });
	};
	const rejectProjectInvitation = async (id) => {
		await client.delete("/manage/current-user/projects-invitations/{id}", { path: { id } });
	};
	const getProjectJoinRequests = async (signal) => {
		const { data } = await client.get("/manage/current-user/projects-join-requests", {}, { signal });
		return data;
	};
	const getProjectJoinRequest = async (id, signal) => {
		const { data } = await client.get("/manage/current-user/projects-join-requests/{id}", { path: { id } }, { signal });
		return data;
	};
	const deleteProjectJoinRequest = async (id) => {
		await client.delete("/manage/current-user/projects-join-requests/{id}", { path: { id } });
	};
	return {
		getMaintainerInvitations,
		getMaintainerInvitation,
		acceptMaintainerInvitation,
		rejectMaintainerInvitation,
		getOrganizationInvitations,
		getOrganizationInvitation,
		acceptOrganizationInvitation,
		rejectOrganizationInvitation,
		getProjectInvitations,
		getProjectInvitation,
		acceptProjectInvitation,
		rejectProjectInvitation,
		getProjectJoinRequests,
		getProjectJoinRequest,
		deleteProjectJoinRequest
	};
};
//#endregion
//#region src/clients/management/deletedProjects/deletedProjects.ts
const createDeletedProjects = (client) => {
	/** One page of deleted projects; `meta.total` counts every row matching the filters. */
	const getDeletedProjects = async (query, signal) => {
		const { data } = await client.get("/manage/deleted-projects", { query: {
			filter: query?.filter,
			page: query?.page
		} }, { signal });
		return data;
	};
	/** Global deleted / purged / overdue counts, independent of any list filter. */
	const getDeletedProjectsStats = async (signal) => {
		const { data } = await client.get("/manage/deleted-projects/stats", {}, { signal });
		return data;
	};
	const getDeletedProject = async (projectId, signal) => {
		const { data } = await client.get("/manage/deleted-projects/{projectId}", { path: { projectId } }, { signal });
		return data;
	};
	/** Restore a deleted project (cancels deletion). Maps to DELETE /manage/deleted-projects/{projectId}
	* — note: the RFC says POST but the live schema uses DELETE. */
	const restoreDeletedProject = async (projectId) => {
		await client.delete("/manage/deleted-projects/{projectId}", { path: { projectId } });
	};
	/** Permanently purge a deleted project (irreversible). Pass `ignoreBackendErrors: true` to purge
	* even if backend-storage cleanup fails. Returns the enqueued purge command. */
	const purgeDeletedProject = async (projectId, ignoreBackendErrors = false) => {
		const { data } = await client.post("/manage/deleted-projects/{projectId}/purge", {
			path: { projectId },
			body: { ignoreBackendErrors }
		});
		return data;
	};
	return {
		getDeletedProjects,
		getDeletedProjectsStats,
		getDeletedProject,
		restoreDeletedProject,
		purgeDeletedProject
	};
};
//#endregion
//#region src/clients/management/features/features.ts
const createFeatures = (client) => {
	const getAllFeatures = async (query, signal) => {
		const { data } = await client.get("/manage/features", { query }, { signal });
		return data;
	};
	const getFeature = async (featureId, signal) => {
		const { data } = await client.get("/manage/features/{id}", { path: { id: featureId } }, { signal });
		return data;
	};
	const getFeatureProjects = async (featureId, signal) => {
		const { data } = await client.get("/manage/features/{id}/projects", { path: { id: featureId } }, { signal });
		return data;
	};
	const getFeatureAdmins = async (featureId, signal) => {
		const { data } = await client.get("/manage/features/{id}/admins", { path: { id: featureId } }, { signal });
		return data;
	};
	return {
		getAllFeatures,
		getFeature,
		getFeatureProjects,
		getFeatureAdmins
	};
};
//#endregion
//#region src/clients/management/fileStorage/fileStorage.ts
const createFileStorage = (client) => {
	const getFileStoragesS3 = async (signal) => {
		const { data } = await client.get("/manage/file-storage-s3", {}, { signal });
		return data;
	};
	const getFileStoragesAbs = async (signal) => {
		const { data } = await client.get("/manage/file-storage-abs", {}, { signal });
		return data;
	};
	const getFileStoragesGcs = async (signal) => {
		const { data } = await client.get("/manage/file-storage-gcs", {}, { signal });
		return data;
	};
	const createFileStorageS3 = async (body) => {
		const { data } = await client.post("/manage/file-storage-s3", { body });
		return data;
	};
	const rotateFileStorageS3Credentials = async ({ fileStorageId, ...body }) => {
		const { data } = await client.post("/manage/file-storage-s3/{fileStorageId}/credentials", {
			path: { fileStorageId },
			body
		});
		return data;
	};
	const setFileStorageS3Default = async (fileStorageId) => {
		const { data } = await client.post("/manage/file-storage-s3/{fileStorageId}/default", { path: { fileStorageId } });
		return data;
	};
	const createFileStorageAbs = async (body) => {
		const { data } = await client.post("/manage/file-storage-abs", { body });
		return data;
	};
	const rotateFileStorageAbsCredentials = async ({ fileStorageId, ...body }) => {
		const { data } = await client.post("/manage/file-storage-abs/{fileStorageId}/credentials", {
			path: { fileStorageId },
			body
		});
		return data;
	};
	const setFileStorageAbsDefault = async (fileStorageId) => {
		const { data } = await client.post("/manage/file-storage-abs/{fileStorageId}/default", { path: { fileStorageId } });
		return data;
	};
	const createFileStorageGcs = async (body) => {
		const { data } = await client.post("/manage/file-storage-gcs", { body });
		return data;
	};
	const rotateFileStorageGcsCredentials = async ({ fileStorageId, ...body }) => {
		const { data } = await client.post("/manage/file-storage-gcs/{fileStorageId}/credentials", {
			path: { fileStorageId },
			body
		});
		return data;
	};
	const setFileStorageGcsDefault = async (fileStorageId) => {
		const { data } = await client.post("/manage/file-storage-gcs/{fileStorageId}/default", { path: { fileStorageId } });
		return data;
	};
	/**
	* Remove a file storage of any provider.
	*
	* - Only the record goes; the bucket, its credentials and a GCS Snowflake integration stay.
	* - 400 while a live project or workspace still references it, naming their ids.
	* - Any maintainer defaulting to it has that default cleared.
	*/
	const deleteFileStorage = async (fileStorageId) => {
		await client.delete("/manage/file-storage/{fileStorageId}", { path: { fileStorageId } });
	};
	/**
	* Change the owner or region of a file storage of any provider.
	*
	* - A new region moves only the record; the files stay where they are.
	* - 400 when the storage is its region's default and the target region already has one.
	*/
	const updateFileStorage = async (fileStorageId, body) => {
		const { data } = await client.patch("/manage/file-storage/{fileStorageId}", {
			path: { fileStorageId },
			body
		});
		return data;
	};
	return {
		deleteFileStorage,
		updateFileStorage,
		getFileStoragesS3,
		getFileStoragesAbs,
		getFileStoragesGcs,
		createFileStorageS3,
		createFileStorageAbs,
		createFileStorageGcs,
		rotateFileStorageS3Credentials,
		rotateFileStorageAbsCredentials,
		rotateFileStorageGcsCredentials,
		setFileStorageS3Default,
		setFileStorageAbsDefault,
		setFileStorageGcsDefault
	};
};
//#endregion
//#region src/clients/management/gcpPartner/gcpPartner.ts
const createGcpPartner = (client) => {
	/** Answers with the job that provisions the backend, not the finished backend. */
	const setUpBigQuery = async (projectId, body, signal) => {
		const { data } = await client.post("/integration/partners/gcp/project/{projectId}/set-up/big-query", {
			path: { projectId },
			body
		}, { signal });
		return data;
	};
	return { setUpBigQuery };
};
//#endregion
//#region src/clients/management/maintainers/maintainers.ts
const createMaintainers = (client) => {
	const getMaintainers = async (signal) => {
		const { data } = await client.get("/manage/maintainers", {}, { signal });
		return data;
	};
	const getMaintainer = async (maintainerId, signal) => {
		const { data } = await client.get("/manage/maintainers/{id}", { path: { id: maintainerId } }, { signal });
		return data;
	};
	const createMaintainer = async (body) => {
		const { data } = await client.post("/manage/maintainers", { body });
		return data;
	};
	const updateMaintainer = async ({ maintainerId, ...body }) => {
		const { data } = await client.patch("/manage/maintainers/{id}", {
			path: { id: maintainerId },
			body
		});
		return data;
	};
	const deleteMaintainer = async (maintainerId) => {
		await client.delete("/manage/maintainers/{id}", { path: { id: maintainerId } });
	};
	const getMaintainerUsers = async (maintainerId, signal) => {
		const { data } = await client.get("/manage/maintainers/{id}/users", { path: { id: maintainerId } }, { signal });
		return data;
	};
	const addMaintainerUser = async ({ maintainerId, ...body }) => {
		const { data } = await client.post("/manage/maintainers/{id}/users", {
			path: { id: maintainerId },
			body
		});
		return data;
	};
	const removeMaintainerUser = async ({ maintainerId, adminId }) => {
		await client.delete("/manage/maintainers/{id}/users/{adminId}", { path: {
			id: maintainerId,
			adminId
		} });
	};
	const getMaintainerInvitations = async (maintainerId, signal) => {
		const { data } = await client.get("/manage/maintainers/{id}/invitations", { path: { id: maintainerId } }, { signal });
		return data;
	};
	const getMaintainerInvitation = async ({ maintainerId, invitationId }, signal) => {
		const { data } = await client.get("/manage/maintainers/{id}/invitations/{invitationId}", { path: {
			id: maintainerId,
			invitationId
		} }, { signal });
		return data;
	};
	const createMaintainerInvitation = async ({ maintainerId, ...body }) => {
		const { data } = await client.post("/manage/maintainers/{id}/invitations", {
			path: { id: maintainerId },
			body
		});
		return data;
	};
	const deleteMaintainerInvitation = async ({ maintainerId, invitationId }) => {
		await client.delete("/manage/maintainers/{id}/invitations/{invitationId}", { path: {
			id: maintainerId,
			invitationId
		} });
	};
	const createMaintainerOrganization = async ({ maintainerId, ...body }) => {
		const { data } = await client.post("/manage/maintainers/{id}/organizations", {
			path: { id: maintainerId },
			body
		});
		return data;
	};
	const getMaintainerMetadata = async (maintainerId, signal) => {
		const { data } = await client.get("/manage/maintainers/{id}/metadata", { path: { id: maintainerId } }, { signal });
		return data;
	};
	const setMaintainerMetadata = async ({ maintainerId, ...body }) => {
		const { data } = await client.post("/manage/maintainers/{id}/metadata", {
			path: { id: maintainerId },
			body
		});
		return data;
	};
	const deleteMaintainerMetadata = async ({ maintainerId, metadataId }) => {
		await client.delete("/manage/maintainers/{id}/metadata/{metadataId}", { path: {
			id: maintainerId,
			metadataId
		} });
	};
	const getMaintainerOrganizations = async (maintainerId, signal) => {
		const { data } = await client.get("/manage/maintainers/{id}/organizations", { path: { id: maintainerId } }, { signal });
		return data;
	};
	const joinMaintainer = async (maintainerId) => {
		await client.post("/manage/maintainers/{id}/join-maintainer", { path: { id: maintainerId } });
	};
	const getMaintainerSamlConfigs = async (maintainerId, signal) => {
		const { data } = await client.get("/manage/maintainers/{maintainerId}/saml", { path: { maintainerId } }, { signal });
		return data;
	};
	const getMaintainerSamlConfig = async ({ maintainerId, configId }, signal) => {
		const { data } = await client.get("/manage/maintainers/{maintainerId}/saml/{configId}", { path: {
			maintainerId,
			configId
		} }, { signal });
		return data;
	};
	const createMaintainerSamlConfig = async ({ maintainerId, ...body }) => {
		const { data } = await client.post("/manage/maintainers/{maintainerId}/saml", {
			path: { maintainerId },
			body
		});
		return data;
	};
	const updateMaintainerSamlConfig = async ({ maintainerId, configId, ...body }) => {
		const { data } = await client.patch("/manage/maintainers/{maintainerId}/saml/{configId}", {
			path: {
				maintainerId,
				configId
			},
			body
		});
		return data;
	};
	const completeMaintainerSamlSetup = async ({ maintainerId, configId, ...body }) => {
		const { data } = await client.post("/manage/maintainers/{maintainerId}/saml/{configId}/complete-setup", {
			path: {
				maintainerId,
				configId
			},
			body
		});
		return data;
	};
	const deleteMaintainerSamlConfig = async ({ maintainerId, configId }) => {
		await client.delete("/manage/maintainers/{maintainerId}/saml/{configId}", { path: {
			maintainerId,
			configId
		} });
	};
	return {
		getMaintainers,
		getMaintainer,
		createMaintainer,
		updateMaintainer,
		deleteMaintainer,
		getMaintainerUsers,
		addMaintainerUser,
		removeMaintainerUser,
		getMaintainerInvitations,
		getMaintainerInvitation,
		createMaintainerInvitation,
		deleteMaintainerInvitation,
		createMaintainerOrganization,
		getMaintainerMetadata,
		setMaintainerMetadata,
		deleteMaintainerMetadata,
		getMaintainerOrganizations,
		joinMaintainer,
		getMaintainerSamlConfigs,
		getMaintainerSamlConfig,
		createMaintainerSamlConfig,
		updateMaintainerSamlConfig,
		completeMaintainerSamlSetup,
		deleteMaintainerSamlConfig
	};
};
//#endregion
//#region src/clients/management/organizations/organizations.ts
const createOrganizations = (client) => {
	const getOrganizations = async (signal) => {
		const { data } = await client.get("/manage/organizations", {}, { signal });
		return data;
	};
	const getOrganization = async (organizationId, signal) => {
		const { data } = await client.get("/manage/organizations/{id}", { path: { id: organizationId } }, { signal });
		return data;
	};
	const updateOrganization = async ({ organizationId, ...body }) => {
		const { data } = await client.patch("/manage/organizations/{id}", {
			path: { id: organizationId },
			body
		});
		return data;
	};
	const deleteOrganization = async (organizationId) => {
		await client.delete("/manage/organizations/{id}", { path: { id: organizationId } });
	};
	const forceMfa = async (organizationId) => {
		const { data } = await client.patch("/manage/organizations/{id}/force-mfa", { path: { id: organizationId } });
		return data;
	};
	const joinOrganization = async (organizationId) => {
		await client.post("/manage/organizations/{id}/join-organization", { path: { id: organizationId } });
	};
	const getOrganizationUsers = async (organizationId, signal) => {
		const { data } = await client.get("/manage/organizations/{id}/users", { path: { id: organizationId } }, { signal });
		return data;
	};
	const addOrganizationUser = async ({ organizationId, ...body }) => {
		const { data } = await client.post("/manage/organizations/{id}/users", {
			path: { id: organizationId },
			body
		});
		return data;
	};
	const removeOrganizationUser = async ({ organizationId, userId }) => {
		await client.delete("/manage/organizations/{id}/users/{userId}", { path: {
			id: organizationId,
			userId
		} });
	};
	const getOrganizationProjectsUsers = async (organizationId, signal) => {
		const { data } = await client.get("/manage/organizations/{id}/projects-users", { path: { id: organizationId } }, { signal });
		return data;
	};
	const getOrganizationInvitations = async (organizationId, signal) => {
		const { data } = await client.get("/manage/organizations/{id}/invitations", { path: { id: organizationId } }, { signal });
		return data;
	};
	const getOrganizationInvitation = async ({ organizationId, invitationId }, signal) => {
		const { data } = await client.get("/manage/organizations/{id}/invitations/{invitationId}", { path: {
			id: organizationId,
			invitationId
		} }, { signal });
		return data;
	};
	const createOrganizationInvitation = async ({ organizationId, ...body }) => {
		const { data } = await client.post("/manage/organizations/{id}/invitations", {
			path: { id: organizationId },
			body
		});
		return data;
	};
	const deleteOrganizationInvitation = async ({ organizationId, invitationId }) => {
		await client.delete("/manage/organizations/{id}/invitations/{invitationId}", { path: {
			id: organizationId,
			invitationId
		} });
	};
	const getOrganizationProjects = async (organizationId, signal) => {
		const { data } = await client.get("/manage/organizations/{id}/projects", { path: { id: organizationId } }, { signal });
		return data;
	};
	const createOrganizationProject = async ({ organizationId, ...body }) => {
		const { data } = await client.post("/manage/organizations/{id}/projects", {
			path: { id: organizationId },
			body
		});
		return data;
	};
	const getOrganizationMetadata = async (organizationId, signal) => {
		const { data } = await client.get("/manage/organizations/{id}/metadata", { path: { id: organizationId } }, { signal });
		return data;
	};
	const setOrganizationMetadata = async ({ organizationId, ...body }) => {
		const { data } = await client.post("/manage/organizations/{id}/metadata", {
			path: { id: organizationId },
			body
		});
		return data;
	};
	const deleteOrganizationMetadata = async ({ organizationId, metadataId }) => {
		await client.delete("/manage/organizations/{id}/metadata/{metadataId}", { path: {
			id: organizationId,
			metadataId
		} });
	};
	return {
		getOrganizations,
		getOrganization,
		updateOrganization,
		deleteOrganization,
		forceMfa,
		joinOrganization,
		getOrganizationUsers,
		addOrganizationUser,
		removeOrganizationUser,
		getOrganizationProjectsUsers,
		getOrganizationInvitations,
		getOrganizationInvitation,
		createOrganizationInvitation,
		deleteOrganizationInvitation,
		getOrganizationProjects,
		createOrganizationProject,
		getOrganizationMetadata,
		setOrganizationMetadata,
		deleteOrganizationMetadata
	};
};
//#endregion
//#region src/clients/management/payAsYouGo/payAsYouGo.ts
const createPayAsYouGo = (client) => {
	const getBillingConfiguration = async (signal) => {
		const { data } = await client.get("/pay-as-you-go/billing/configuration", {}, { signal });
		return data;
	};
	const getCreditPurchases = async (idProject, signal) => {
		const { data } = await client.post("/pay-as-you-go/billing/credits", { body: { idProject } }, { signal });
		return data;
	};
	const getStripeCustomer = async (idProject, signal) => {
		const { data } = await client.get("/pay-as-you-go/billing/stripe-customer", { query: { idProject } }, { signal });
		return data;
	};
	const updateStripeCustomer = async (body) => {
		const { data } = await client.put("/pay-as-you-go/billing/stripe-customer", { body });
		return data;
	};
	const getTopUpSetting = async (projectId, signal) => {
		const { data } = await client.get("/pay-as-you-go/billing/top-up/{projectId}", { path: { projectId } }, { signal });
		return data;
	};
	const updateTopUpSetting = async (body) => {
		await client.put("/pay-as-you-go/billing/top-up", { body });
	};
	const createPaymentRequest = async (body) => {
		const { data } = await client.post("/pay-as-you-go/billing/create-payment-request", { body });
		return data;
	};
	return {
		getBillingConfiguration,
		getCreditPurchases,
		getStripeCustomer,
		updateStripeCustomer,
		getTopUpSetting,
		updateTopUpSetting,
		createPaymentRequest
	};
};
//#endregion
//#region src/clients/management/programmaticProjects/programmaticProjects.ts
const createProgrammaticProjects = (client, genericClient) => {
	const getClaim = async (signal) => {
		const { data } = await client.get("/manage/programmatic-projects/claim", {}, { signal });
		return data;
	};
	/**
	* Redeem an agent project's claim, making the bearer's account its owner.
	*
	* - The session decides who becomes the owner; the payload only proves possession of the link.
	* - Single-use and irreversible: the claim is consumed before ownership is granted, so a second
	*   call is a 400, never an idempotent success. Callers must not retry.
	* - A 400 covers malformed, unknown, expired, already-redeemed and never-provisioned alike. The
	*   backend collapses them on purpose, because the claim is bearer-only.
	*/
	const confirm = async (payload, signal) => {
		const { data } = await genericClient.post("/manage/programmatic-projects/confirm", { body: payload }, { signal });
		return data;
	};
	return {
		getClaim,
		confirm
	};
};
//#endregion
//#region src/clients/management/projects/projects.ts
const createProjects = (client) => {
	const getProject = async (projectId, signal) => {
		const { data } = await client.get("/manage/projects/{projectId}", { path: { projectId: Number(projectId) } }, { signal });
		return data;
	};
	const getProjectTemplates = async (signal) => {
		const { data } = await client.get("/manage/project-templates", {}, { signal });
		return data;
	};
	const addProjectFeature = async ({ projectId, feature }) => {
		const { data } = await client.post("/manage/projects/{projectId}/features", {
			path: { projectId },
			body: { feature }
		});
		return data;
	};
	const removeProjectFeature = async ({ projectId, feature }) => {
		await client.delete("/manage/projects/{projectId}/features/{feature}", { path: {
			projectId,
			feature
		} });
		return null;
	};
	const changeProjectUserRole = async ({ projectId, userId, role }) => {
		const { data } = await client.patch("/manage/projects/{projectId}/users/{userId}", {
			path: {
				projectId,
				userId
			},
			body: { role }
		});
		return data;
	};
	const getProjectUsers = async (projectId, signal) => {
		const { data } = await client.get("/manage/projects/{projectId}/users", { path: { projectId: Number(projectId) } }, { signal });
		return data;
	};
	const getProjectInvitations = async (projectId, signal) => {
		const { data } = await client.get("/manage/projects/{projectId}/invitations", { path: { projectId: Number(projectId) } }, { signal });
		return data;
	};
	const createProjectInvitation = async ({ projectId, ...body }) => {
		const { data } = await client.post("/manage/projects/{projectId}/invitations", {
			path: { projectId },
			body: {
				...body,
				role: body.role
			}
		});
		return data;
	};
	const deleteProjectInvitation = async ({ projectId, invitationId }) => {
		await client.delete("/manage/projects/{projectId}/invitations/{invitationId}", { path: {
			projectId,
			invitationId
		} });
		return null;
	};
	const addProjectUser = async ({ projectId, ...body }) => {
		await client.post("/manage/projects/{projectId}/users", {
			path: { projectId },
			body
		});
		return null;
	};
	const removeProjectUser = async ({ projectId, idOrEmail }) => {
		await client.delete("/manage/projects/{projectId}/users/{idOrEmail}", { path: {
			projectId,
			idOrEmail: String(idOrEmail)
		} });
		return null;
	};
	const getProjectJoinRequests = async (projectId, signal) => {
		const { data } = await client.get("/manage/projects/{projectId}/join-requests", { path: { projectId: Number(projectId) } }, { signal });
		return data;
	};
	const approveProjectJoinRequest = async ({ projectId, joinRequestId }) => {
		await client.put("/manage/projects/{projectId}/join-requests/{joinRequestId}", { path: {
			projectId,
			joinRequestId
		} });
		return null;
	};
	const rejectProjectJoinRequest = async ({ projectId, joinRequestId }) => {
		await client.delete("/manage/projects/{projectId}/join-requests/{joinRequestId}", { path: {
			projectId,
			joinRequestId
		} });
		return null;
	};
	const getProjectMetadata = async (projectId, signal) => {
		const { data } = await client.get("/manage/projects/{projectId}/metadata", { path: { projectId } }, { signal });
		return data;
	};
	const setProjectMetadata = async ({ projectId, provider, metadata }) => {
		const { data } = await client.post("/manage/projects/{projectId}/metadata", {
			path: { projectId },
			body: {
				provider,
				metadata
			}
		});
		return data;
	};
	const getProjectReviewers = async (projectId, signal) => {
		const { data } = await client.get("/manage/projects/{projectId}/reviewers", { path: { projectId } }, { signal });
		return data;
	};
	const setProjectReviewers = async ({ projectId, adminIds }) => {
		await client.put("/manage/projects/{projectId}/reviewers", {
			path: { projectId },
			body: { adminIds }
		});
	};
	const updateProject = async ({ projectId, ...body }) => {
		const { data } = await client.put("/manage/projects/{projectId}", {
			path: { projectId },
			body
		});
		return data;
	};
	const deleteProject = async (projectId) => {
		await client.delete("/manage/projects/{projectId}", { path: { projectId } });
		return null;
	};
	const moveProject = async ({ projectId, organizationId }) => {
		const { data } = await client.post("/manage/projects/{projectId}/organizations", {
			path: { projectId },
			body: { organizationId }
		});
		return data;
	};
	const setProjectDisabled = async ({ projectId, ...body }) => {
		const { data } = await client.post("/manage/projects/{projectId}/disabled", {
			path: { projectId },
			body
		});
		return data;
	};
	const setProjectLimits = async ({ projectId, ...body }) => {
		const { data } = await client.post("/manage/projects/{projectId}/limits", {
			path: { projectId },
			body
		});
		return data;
	};
	const getProjectTemplate = async (templateId, signal) => {
		const { data } = await client.get("/manage/project-templates/{templateId}", { path: { templateId } }, { signal });
		return data;
	};
	const getProjectTemplateFeatures = async (templateId, signal) => {
		const { data } = await client.get("/manage/project-templates/{templateId}/features", { path: { templateId } }, { signal });
		return data;
	};
	const addProjectTemplateFeature = async ({ templateId, ...body }) => {
		await client.post("/manage/project-templates/{templateId}/features", {
			path: { templateId },
			body
		});
	};
	const removeProjectTemplateFeature = async ({ templateId, featureName }) => {
		await client.delete("/manage/project-templates/{templateId}/features/{featureName}", { path: {
			templateId,
			featureName
		} });
	};
	const getProjectInvitation = async ({ projectId, invitationId }, signal) => {
		const { data } = await client.get("/manage/projects/{projectId}/invitations/{invitationId}", { path: {
			projectId,
			invitationId
		} }, { signal });
		return data;
	};
	const getProjectJoinRequest = async ({ projectId, joinRequestId }, signal) => {
		const { data } = await client.get("/manage/projects/{projectId}/join-requests/{joinRequestId}", { path: {
			projectId,
			joinRequestId
		} }, { signal });
		return data;
	};
	const joinProject = async ({ projectId, ...body }) => {
		await client.post("/manage/projects/{projectId}/join-project", {
			path: { projectId },
			body
		});
	};
	const requestProjectAccess = async ({ projectId, ...body }) => {
		const { data } = await client.post("/manage/projects/{projectId}/request-access", {
			path: { projectId },
			body
		});
		return data;
	};
	const removeProjectLimit = async ({ projectId, limitName }) => {
		await client.delete("/manage/projects/{projectId}/limits/{limitName}", { path: {
			projectId,
			limitName
		} });
	};
	const deleteProjectMetadata = async ({ projectId, metadataId }) => {
		await client.delete("/manage/projects/{projectId}/metadata/{metadataId}", { path: {
			projectId,
			metadataId
		} });
	};
	return {
		getProject,
		getProjectTemplates,
		addProjectFeature,
		removeProjectFeature,
		changeProjectUserRole,
		getProjectUsers,
		getProjectInvitations,
		createProjectInvitation,
		deleteProjectInvitation,
		addProjectUser,
		removeProjectUser,
		getProjectJoinRequests,
		approveProjectJoinRequest,
		rejectProjectJoinRequest,
		getProjectMetadata,
		setProjectMetadata,
		getProjectReviewers,
		setProjectReviewers,
		updateProject,
		deleteProject,
		moveProject,
		setProjectDisabled,
		setProjectLimits,
		getProjectTemplate,
		getProjectTemplateFeatures,
		addProjectTemplateFeature,
		removeProjectTemplateFeature,
		getProjectInvitation,
		getProjectJoinRequest,
		joinProject,
		requestProjectAccess,
		removeProjectLimit,
		deleteProjectMetadata
	};
};
//#endregion
//#region src/clients/management/snowflakePartnerConnect/snowflakePartnerConnect.ts
const createSnowflakePartnerConnect = (client) => {
	/** `queriesToRun` lists what the Snowflake account admin still has to run before `unlock`. */
	const check = async (projectId, signal) => {
		const { data } = await client.get("/snowflake-partner-connect/project/{projectId}/check", { path: { projectId } }, { signal });
		return data;
	};
	/** A failed grant test answers `400` with the same `status`/`message` body. */
	const unlock = async (projectId, signal) => {
		const { data } = await client.post("/snowflake-partner-connect/project/{projectId}/unlock", { path: { projectId } }, { signal });
		return data;
	};
	return {
		check,
		unlock
	};
};
//#endregion
//#region src/clients/management/storageBackends/storageBackends.ts
const createStorageBackends = (client) => {
	const getStorageBackend = async (id, signal) => {
		const { data } = await client.get("/manage/storage-backend/{id}", { path: { id } }, { signal });
		return data;
	};
	const getStorageBackends = async (signal) => {
		const { data } = await client.get("/manage/storage-backend", {}, { signal });
		return data;
	};
	/** Projects assigned to this backend, paginated via `limit`/`offset`. */
	const getStorageBackendProjects = async (id, query, signal) => {
		const { data } = await client.get("/manage/storage-backend/{id}/projects", {
			path: { id },
			query: {
				limit: query?.limit,
				offset: query?.offset
			}
		}, { signal });
		return data;
	};
	/** Maintainers assigned to this backend — small, unpaginated list. */
	const getStorageBackendMaintainers = async (id, signal) => {
		const { data } = await client.get("/manage/storage-backend/{id}/maintainers", { path: { id } }, { signal });
		return data;
	};
	/** The guide a BigQuery backend's customer follows to grant Keboola policy-tag access. */
	const getPolicyTagEnablementGuide = async ({ id, ...query }, signal) => {
		const { data } = await client.get("/manage/storage-backend/{id}/policy-tags/enablement-guide", {
			path: { id },
			query
		}, {
			signal,
			queryArrayFormat: "brackets"
		});
		return data;
	};
	const createSnowflakeStorageBackend = async (body) => {
		const { data } = await client.post("/manage/storage-backend/snowflake", { body });
		return data;
	};
	const createBigqueryStorageBackend = async (body) => {
		const { data } = await client.post("/manage/storage-backend/bigquery", { body });
		return data;
	};
	const createBigqueryServerSideStorageBackend = async (body) => {
		const { data } = await client.post("/manage/storage-backend/bigquery/server-side", { body });
		return data;
	};
	const updateStorageBackend = async ({ id, ...body }) => {
		const { data } = await client.patch("/manage/storage-backend/{id}", {
			path: { id },
			body
		});
		return data;
	};
	const updateBigqueryStorageBackend = async ({ id, ...body }) => {
		const { data } = await client.patch("/manage/storage-backend/{id}/bigquery", {
			path: { id },
			body
		});
		return data;
	};
	const activateStorageBackend = async (backendId) => {
		const { data } = await client.post("/manage/storage-backend/{backendId}/activate", { path: { backendId } });
		return data;
	};
	const deleteStorageBackend = async (id) => {
		await client.delete("/manage/storage-backend/{id}", { path: { id } });
	};
	return {
		getStorageBackend,
		getStorageBackends,
		getStorageBackendProjects,
		getStorageBackendMaintainers,
		getPolicyTagEnablementGuide,
		createSnowflakeStorageBackend,
		createBigqueryStorageBackend,
		createBigqueryServerSideStorageBackend,
		updateStorageBackend,
		updateBigqueryStorageBackend,
		activateStorageBackend,
		deleteStorageBackend
	};
};
//#endregion
//#region src/clients/management/uiApps/uiApps.ts
const createUiApps = (client) => {
	const getUiApps = async (query, signal) => {
		const { data } = await client.get("/manage/ui-apps", { query: query?.includeInactive ? { includeInactive: true } : {} }, { signal });
		return data;
	};
	/** `stringId`, not the numeric id — the API keys UI applications on the immutable manifest name. */
	const getUiApp = async (stringId, signal) => {
		const { data } = await client.get("/manage/ui-apps/{id}", { path: { id: stringId } }, { signal });
		return data;
	};
	const registerUiApp = async (body) => {
		const { data } = await client.post("/manage/ui-apps", { body });
		return data;
	};
	const renameUiApp = async ({ stringId, ...body }) => {
		await client.patch("/manage/ui-apps/{id}", {
			path: { id: stringId },
			body
		});
	};
	const activateUiAppVersion = async ({ stringId, version }) => {
		await client.post("/manage/ui-apps/{id}/activate/{version}", { path: {
			id: stringId,
			version
		} });
	};
	const deleteUiApp = async (stringId) => {
		await client.delete("/manage/ui-apps/{id}", { path: { id: stringId } });
	};
	return {
		getUiApps,
		getUiApp,
		registerUiApp,
		renameUiApp,
		activateUiAppVersion,
		deleteUiApp
	};
};
//#endregion
//#region src/clients/management/users/users.ts
const createUsers = (client) => {
	const addUserAdminFeature = async ({ feature, userIdOrMail }, signal) => {
		const { data } = await client.post("/manage/users/{idOrEmail}/features", {
			path: { idOrEmail: userIdOrMail },
			body: { feature }
		}, { signal });
		return data;
	};
	const removeUserAdminFeature = async ({ userIdOrMail, feature }, signal) => {
		const { data } = await client.delete("/manage/users/{idOrEmail}/features/{featureName}", { path: {
			idOrEmail: userIdOrMail,
			featureName: feature
		} }, { signal });
		return data;
	};
	/** Paginated list of all users, filterable via `q` (name/email substring). */
	const getUsers = async (query, signal) => {
		const { data } = await client.get("/manage/users", { query: {
			q: query?.q,
			limit: query?.limit,
			offset: query?.offset
		} }, { signal });
		return data;
	};
	const getUser = async (id, signal) => {
		const { data } = await client.get("/manage/users/{idOrEmail}", { path: { idOrEmail: String(id) } }, { signal });
		return data;
	};
	const getUserOrganizations = async (id, signal) => {
		const { data } = await client.get("/manage/users/{id}/organizations", { path: { id } }, { signal });
		return data;
	};
	const getUserProjects = async (id, signal) => {
		const { data } = await client.get("/manage/users/{id}/projects", { path: { id } }, { signal });
		return data;
	};
	const getUserMaintainers = async (id, signal) => {
		const { data } = await client.get("/manage/users/{id}/maintainers", { path: { id } }, { signal });
		return data;
	};
	/** Requires the target user to have MFA enabled (422 otherwise). Superadmin-only. */
	const grantSuperAdmin = async (id, signal) => {
		const { data } = await client.post("/manage/users/{id}/super-admin", { path: { id } }, { signal });
		return data;
	};
	const removeSuperAdmin = async (id, signal) => {
		const { data } = await client.delete("/manage/users/{idOrEmail}/super-admin", { path: { idOrEmail: String(id) } }, { signal });
		return data;
	};
	const disableUserMfa = async (id, signal) => {
		await client.delete("/manage/users/{idOrEmail}/mfa", { path: { idOrEmail: String(id) } }, { signal });
	};
	const deleteUser = async (id, signal) => {
		await client.delete("/manage/users/{idOrEmail}", { path: { idOrEmail: String(id) } }, { signal });
	};
	const updateUser = async ({ id, ...body }) => {
		const { data } = await client.put("/manage/users/{idOrEmail}", {
			path: { idOrEmail: String(id) },
			body
		});
		return data;
	};
	const getUserMetadata = async (id, signal) => {
		const { data } = await client.get("/manage/users/{idOrEmail}/metadata", { path: { idOrEmail: String(id) } }, { signal });
		return data;
	};
	const setUserMetadata = async ({ id, ...body }) => {
		const { data } = await client.post("/manage/users/{idOrEmail}/metadata", {
			path: { idOrEmail: String(id) },
			body
		});
		return data;
	};
	const deleteUserMetadata = async ({ id, metadataId }) => {
		await client.delete("/manage/users/{idOrEmail}/metadata/{metadataId}", { path: {
			idOrEmail: String(id),
			metadataId
		} });
	};
	return {
		addUserAdminFeature,
		removeUserAdminFeature,
		getUsers,
		getUser,
		getUserOrganizations,
		getUserProjects,
		getUserMaintainers,
		grantSuperAdmin,
		removeSuperAdmin,
		disableUserMfa,
		deleteUser,
		updateUser,
		getUserMetadata,
		setUserMetadata,
		deleteUserMetadata
	};
};
//#endregion
//#region src/clients/management/workspaces/workspaces.ts
const createWorkspaces = (client) => {
	/**
	* Reactivate a workspace that retention deactivated for inactivity. Super-admin only.
	*
	* - 400 covers "not deactivated" and "not eligible" alike — the backend collapses both, so the
	*   caller reads the message rather than branching on a code.
	*/
	const reactivateWorkspace = async (workspaceId, signal) => {
		const { data } = await client.post("/manage/workspaces/{workspaceId}/reactivate", { path: { workspaceId } }, { signal });
		return data;
	};
	/**
	* List a project's retention workspaces (warned or already deactivated for inactivity).
	* Super-admin only.
	*/
	const listProjectRetentionWorkspaces = async (projectId, params = {}, signal) => {
		const { data } = await client.get("/manage/projects/{projectId}/workspaces", {
			path: { projectId },
			query: params.state ? { state: params.state } : {}
		}, { signal });
		return data;
	};
	return {
		reactivateWorkspace,
		listProjectRetentionWorkspaces
	};
};
//#endregion
//#region src/clients/management/managementClient.ts
const createAuthHeaders = (auth) => {
	if (auth.type === "management-token") return auth.token ? { [KeboolaHttpHeader.MANAGEMENT_API_TOKEN]: auth.token } : {};
	return { [HttpHeader.AUTHORIZATION]: `Bearer ${auth.token}` };
};
const createManagementClient = ({ baseUrl, middlewares = [], auth, withAuthMiddleware = true }) => {
	const allMiddlewares = withAuthMiddleware && auth.type === "management-token" ? [...middlewares, createAuthMiddleware({
		baseUrl,
		accessToken: auth.token,
		mintSessionToken: auth.mintSessionToken
	})] : middlewares;
	const headers = withAuthMiddleware ? void 0 : createAuthHeaders(auth);
	const client = createOpenapiFetchClient({
		baseUrl,
		middlewares: allMiddlewares,
		headers,
		validateStatus: validateManagementStatus
	});
	const genericClient = createGenericFetchClient({
		baseUrl,
		middlewares
	});
	const authedGenericClient = createGenericFetchClient({
		baseUrl,
		middlewares: allMiddlewares,
		headers,
		validateStatus: validateManagementStatus
	});
	const verifyToken = async (signal) => {
		const { data } = await client.get("/manage/tokens/verify", {}, { signal });
		return data;
	};
	const healthCheck = async (signal) => {
		const { data } = await genericClient.get("/health-check", {}, { signal });
		return data;
	};
	return {
		applicationTokens: createApplicationTokens(client),
		healthCheck,
		currentUser: createCurrentUser(client),
		deletedProjects: createDeletedProjects(client),
		maintainers: createMaintainers(client),
		organizations: createOrganizations(client),
		payAsYouGo: createPayAsYouGo(client),
		features: createFeatures(client),
		fileStorage: createFileStorage(client),
		gcpPartner: createGcpPartner(client),
		projects: createProjects(client),
		snowflakePartnerConnect: createSnowflakePartnerConnect(client),
		programmaticProjects: createProgrammaticProjects(client, authedGenericClient),
		storageBackends: createStorageBackends(client),
		uiApps: createUiApps(client),
		users: createUsers(client),
		verifyToken,
		workspaces: createWorkspaces(authedGenericClient)
	};
};
//#endregion
export { isManagementClientAuthError as n, isManagementSessionMintError as r, createManagementClient as t };

//# sourceMappingURL=management-NYAD6ej4.js.map